// Firebase Configuration and Initialization
import { initializeApp, FirebaseApp, getApp, getApps, deleteApp } from 'firebase/app'
import { getFirestore, Firestore } from 'firebase/firestore'
import { getAuth, Auth, onAuthStateChanged, User } from 'firebase/auth'
import { getStorage, FirebaseStorage } from 'firebase/storage'
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions'

type RequiredFirebaseEnv = {
  configKey: keyof typeof firebaseConfig
  envKey: string
}

// Firebase configuration from Vite environment variables.
// These must be present at build time in production hosting.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
}

const requiredFirebaseEnv: RequiredFirebaseEnv[] = [
  { configKey: 'apiKey', envKey: 'VITE_FIREBASE_API_KEY' },
  { configKey: 'authDomain', envKey: 'VITE_FIREBASE_AUTH_DOMAIN' },
  { configKey: 'projectId', envKey: 'VITE_FIREBASE_PROJECT_ID' },
  { configKey: 'storageBucket', envKey: 'VITE_FIREBASE_STORAGE_BUCKET' },
  { configKey: 'messagingSenderId', envKey: 'VITE_FIREBASE_MESSAGING_SENDER_ID' },
  { configKey: 'appId', envKey: 'VITE_FIREBASE_APP_ID' }
]

const isMissingOrPlaceholderValue = (value: unknown): boolean => {
  const normalizedValue = String(value || '').trim().toLowerCase()
  return (
    !normalizedValue ||
    normalizedValue.includes('your_') ||
    normalizedValue.includes('your-project-id') ||
    normalizedValue === '123456789012' ||
    normalizedValue.includes('abcdef123456')
  )
}

const missingFirebaseEnvKeys = requiredFirebaseEnv
  .filter(({ configKey }) => isMissingOrPlaceholderValue(firebaseConfig[configKey]))
  .map(({ envKey }) => envKey)

export const getMissingFirebaseEnvKeys = (): string[] => missingFirebaseEnvKeys.slice()

export const getFirebaseReadinessErrorMessage = (): string => {
  if (missingFirebaseEnvKeys.length > 0) {
    return `Firebase configuration missing: ${missingFirebaseEnvKeys.join(', ')}`
  }

  return 'Firebase initialization failed. Check the Firebase project env values and reload.'
}

if (missingFirebaseEnvKeys.length > 0) {
  console.error('Missing Firebase env keys:', missingFirebaseEnvKeys)
  throw new Error(getFirebaseReadinessErrorMessage())
}

export const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig)
export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)
export const storage: FirebaseStorage = getStorage(app)

const functionsRegion = import.meta.env.VITE_FUNCTIONS_REGION || 'us-central1'
export const functions: Functions = getFunctions(app, functionsRegion)
if (import.meta.env.VITE_FUNCTIONS_EMULATOR === 'true') {
  const host = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST || 'localhost'
  const port = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT || '5001')
  connectFunctionsEmulator(functions, host, port)
}

console.log('Firebase config projectId:', import.meta.env.VITE_FIREBASE_PROJECT_ID)
console.log('Firebase app:', app)
console.log('Firestore db:', db)
console.log('Auth user:', auth.currentUser)

// Firebase is configured when all required production env vars exist.
export const isFirebaseConfigured = () => missingFirebaseEnvKeys.length === 0

// Helper function to check if Firebase is ready
export const isFirebaseReady = (): boolean => {
  return Boolean(app && db && auth)
}

export const assertFirebaseReady = (): void => {
  if (!isFirebaseReady()) {
    throw new Error(getFirebaseReadinessErrorMessage())
  }
}

// Secondary Firebase app for creating users without affecting main auth session
export const getSecondaryAuth = (): Auth => {
  try {
    const secondaryApp = getApp('secondary')
    return getAuth(secondaryApp)
  } catch {
    const secondaryApp = initializeApp(firebaseConfig, 'secondary')
    return getAuth(secondaryApp)
  }
}

// Clean up secondary app after use
export const cleanupSecondaryAuth = async () => {
  try {
    const secondaryApp = getApp('secondary')
    await deleteApp(secondaryApp)
  } catch {
    // App doesn't exist, nothing to clean up
  }
}

export const waitForAuthenticatedUser = (timeoutMs = 8000): Promise<User | null> => {
  if (auth.currentUser) return Promise.resolve(auth.currentUser)

  return new Promise((resolve) => {
    let settled = false
    let unsubscribe = () => {}
    const timeout = window.setTimeout(() => {
      if (settled) return
      settled = true
      unsubscribe()
      resolve(auth.currentUser || null)
    }, timeoutMs)

    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      unsubscribe()
      resolve(user)
    })
  })
}

// Collection names
export const COLLECTIONS = {
  BUSINESSES: 'businesses',
  PARTIES: 'parties',
  ITEMS: 'items',
  INVOICES: 'invoices',
  EXPENSES: 'expenses',
  SETTINGS: 'settings',
  USERS: 'users',
  DELIVERY_CHALLANS: 'delivery_challans',
  PURCHASE_ORDERS: 'purchase_orders',
  CREDIT_NOTES: 'credit_notes',
  DEBIT_NOTES: 'debit_notes',
  PROFORMA_INVOICES: 'proforma_invoices',
  EWAY_BILLS: 'eway_bills',
  PAYMENTS_IN: 'payments_in',
  PAYMENTS_OUT: 'payments_out',
  PRICE_LISTS: 'price_lists',
  BANKING: 'banking',
  QUOTATIONS: 'quotations',
  // CRM Collections
  CRM_LEADS: 'crm_leads',
  CRM_ACTIVITIES: 'crm_activities',
  CRM_SITE_VISITS: 'crm_site_visits',
  CRM_REQUIREMENTS: 'crm_requirements',
  CRM_DRAWINGS: 'crm_drawings',
  CRM_QUOTATIONS: 'crm_quotations',
  CRM_ATTACHMENTS: 'crm_attachments',
  CRM_AUDIT_LOGS: 'crm_audit_logs',
  CRM_SETTINGS: 'crm_settings',
  // Subscription Collections
  SUBSCRIPTIONS: 'subscriptions',
  BILLING_RECORDS: 'billing_records',
  // Staff Master
  STAFF_MASTER: 'staff_master'
} as const

export type CollectionName = typeof COLLECTIONS[keyof typeof COLLECTIONS]
